/**
 * Common Layout
 */
import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { getRoute, PATH, getDefaultCanonicalUrl } from '@/utils/routes';
import {
  fetchFeatureLocks,
  getB2bRfqInputFields,
  getCustomerReviewSettings,
  saveStoreInfo,
  toggleInaccessibleView,
} from '@/redux/actions';
import LocalStorageHelper from '@/utils/LocalStorageHelper';
import { ThemeCss } from './theme';
import { RichSnippetStoreDetails } from './RichSnippetStoreDetails';
import { useFeatureLocks } from '@/hooks/useFeatureLocks';
import { B2B_STORE_TYPE_CONSTANT } from '@/utils/constants';
import dynamic from 'next/dynamic';
import { useSSRSelector } from '@/redux/ssrStore';
import { DEFAULT_SSR_IMAGE_URL } from '@/utils/constants/images';
import checkIfThemeEditorOrPreview from '@/utils/checkIfThemeEditorOrPreview';
import { getUpdatedGlobalSetting } from '../WidgetMaker/WidgetDnD/utils';
import { useWidgetDndContextData } from 'src/context/WidgetDndContext';
import { FONT_DROPDOWN } from '../WidgetMaker/WidgetDnD/GlobalStyle/globalSettingConstant';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import { GLOBAL_STYLE } from 'src/constants/globalConfigs';
import AnalyticsTags from './component/AnalyticsTags';
import { useCheckIfHomePage } from 'src/utils/useCheckIfHomePage';
import { getWishlistItemCountByUser } from 'src/redux/actions/wishlist';
import { WISHLIST_FEATURE_STATUS } from 'src/constants/wishlist';
import { shouldBlockRobots } from 'src/utils/shouldBlockRobotCrawler';
// import GTMNoScript from './component/AnalyticsTags/GTMNoScript';

const PromoModal = dynamic(
  () => import('../CouponsAndVouchers/PromoModal').then((mod) => mod.default),
  { ssr: false }
);
const PromoRemovalAlertModal = dynamic(
  () => import('../CouponsAndVouchers/PromoRemovalAlertModal').then((mod) => mod.default),
  { ssr: false }
);
const WhatsAppLeadGenerationModal = dynamic(
  () =>
    import('../WhatsAppLeadGenerationModal').then(
      (mod) => mod.WhatsAppLeadGenerationModal
    ),
  { ssr: false }
);
const B2bRfqFormModal = dynamic(
  () =>
    import('@/containers/ProductDescription/Components/B2b/B2bRfqFormModal').then(
      (mod) => mod.B2bRfqFormModal
    ),
  { ssr: false }
);
const RedirectionToWebConsoleModal = dynamic(
  () => import('../RedirectionToWebConsoleModal'),
  { ssr: false }
);
const InaccessibleLayer = dynamic(() => import('../InaccessibleLayer'), { ssr: false });

const ExitGatingModal = dynamic(() => import('../ExitGatingModal'), { ssr: false });

const IS_SERVER = typeof window === 'undefined';

const Layout = ({
  store,
  title = '',
  Icon = '',
  favIcon = '',
  metaDescription = '',
  OGTitle = '',
  OGDescription = '',
  OGImage = '',
  children,
  keywords = '',
  canonicalTagUrl = '',
  ...props
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const isRedirectedToHome = useRef(false);

  const {
    widgetContextState: { globalSettings, isBuilder },
    widgetContextActions: { setGlobalSetting, setGlobalStyle },
  } = useWidgetDndContextData();

  const [showState, setShowState] = useState({
    showInaccessibleView: false,
    showRedirectionModal: false,
  });

  const { storeData, shouldLoadFeatureLocks, globalStyleSSR } = useSSRSelector(
    (state) => ({
      storeData: state.storeReducer.store,
      globalStyleSSR: state.jsonWidgetsReducer.globalStyle || {},
      shouldLoadFeatureLocks: !state.commonReducer.featureLocksData.isFeatureLocksLoaded,
    })
  );

  const { userData, toggleMobileView, isLoginByEmail } = useSSRSelector((state) => ({
    userData: state.userReducer,
    toggleMobileView: state.commonReducer.toggleMobileView,
    isLoginByEmail: !!state.userReducer.data?.email,
  }));

  // Global modal states
  const { showWhatsAppLeadModal, promoRemovalAlertModalState, promoModalState } =
    useSelector((state: any) => ({
      showWhatsAppLeadModal: state.commonReducer?.whatsAppLeadModalState?.show,
      promoRemovalAlertModalState: state.commonReducer?.promoRemovalAlertModalState || {},
      promoModalState: state.commonReducer.promoDetailModalState || {},
    }));

  const storeInfo = store || storeData;
  const storeId = props?.storeInfo?.store_id || storeData?.store_id;
  const hostname = props?.hostName || (IS_SERVER ? '' : window.location.hostname);

  const featureLocksStatus = useFeatureLocks();
  const { memberOnlyLoginFeatureStatus } = featureLocksStatus;
  const isMolUnlocked = memberOnlyLoginFeatureStatus?.isLocked === false;

  const isBrandWebsiteDemo = router.pathname.includes('/brand-website-theme-demo');
  const isAboutPage = router.pathname.includes(`${PATH.DOMAIN_HOME}${PATH.ABOUT_US}`);
  const isHomePage = useCheckIfHomePage();
  const gtmId = store?.store_marketing_tags?.g_tag ?? 'GTM-N8T6CPZ';

  useEffect(() => {
    if (checkIfThemeEditorOrPreview() && !isBrandWebsiteDemo) {
      const bodyElement = document.getElementsByTagName('body')[0];
      if (bodyElement) {
        (bodyElement as any).style = 'pointer-events: none';
      }
    }
    if (
      storeInfo?.services?.store_type_flag === B2B_STORE_TYPE_CONSTANT &&
      userData?.isLogin
    ) {
      dispatch(getB2bRfqInputFields(undefined, storeInfo?.store_id));
    }
    if (
      userData?.isLogin &&
      storeInfo?.services?.wishlist_flag !== WISHLIST_FEATURE_STATUS.WISHLIST_INACTIVE
    ) {
      dispatch(getWishlistItemCountByUser({ store_id: storeInfo?.store_id }));
    }
  }, []);

  useEffect(() => {
    const shouldRedirectToMOL =
      !checkIfThemeEditorOrPreview() &&
      isMolUnlocked &&
      storeInfo?.services?.members_only_access &&
      !LocalStorageHelper.get(`isMemberAllowed&store_id=${storeInfo?.store_id}`) &&
      !router.pathname.includes(PATH.MEMBER_LOGIN) &&
      !router.pathname.includes(PATH.BUILDER_PREVIEW);

    if (shouldRedirectToMOL && !props.isBuilder) {
      const memberLoginRoute = getRoute(PATH.MEMBER_LOGIN, storeInfo?.store_info?.domain);
      router.replace(memberLoginRoute);
    }
  }, [isMolUnlocked]);

  useEffect(() => {
    const globalStyleData = cloneDeep(globalStyleSSR);
    if (globalStyleData && Object.keys(globalStyleData || {})?.length) {
      const globalStyleDataClone = {};
      for (const key in globalStyleData) {
        if (GLOBAL_STYLE[key] && globalStyleData[key])
          globalStyleDataClone[key] = merge(GLOBAL_STYLE[key], globalStyleData[key]);
      }
      setGlobalStyle(globalStyleDataClone);
      const updatedGlobalSetting = getUpdatedGlobalSetting(
        globalStyleDataClone,
        globalSettings
      );
      setGlobalSetting(updatedGlobalSetting);
    }
  }, [globalStyleSSR]);

  /**
   * Instead of putting this sequence of code in every container,
   * we can put it here and reduce the number of lines of code.
   * If there are any special ones, the can be put in respective
   * containers.
   *
   * These four are common and used thorughout the pages and containers
   */
  useEffect(() => {
    /* Save store info */
    if (Object.keys(props?.storeInfo || {}).length) {
      dispatch(saveStoreInfo(props?.storeInfo || {}));
    }
  }, [props?.storeInfo]);

  useEffect(() => {
    if (storeId) {
      dispatch(getCustomerReviewSettings(storeId));
    }
  }, [storeId]);

  useEffect(() => {
    const storeInfo = store || storeData;
    const route = getRoute(PATH.PRODUCT, storeInfo?.store_info?.domain);
    if (
      !isRedirectedToHome.current &&
      storeInfo.store_id &&
      storeInfo.domain_info.status === 2 &&
      !route.includes(location.pathname)
    ) {
      isRedirectedToHome.current = true;
      router.replace(route);
    }
  }, [storeData]);

  useEffect(() => {
    if (storeId && shouldLoadFeatureLocks) dispatch(fetchFeatureLocks(storeId));
  }, [storeId, shouldLoadFeatureLocks]);

  const loginBtnClick = () => {
    setShowState((state) => ({
      ...state,
      showRedirectionModal: true,
    }));
  };

  const showIframeView = () => {
    dispatch(toggleInaccessibleView(true));
    setShowState((state) => ({
      ...state,
      showInaccessibleView: false,
    }));
  };

  function getGoogleSiteVerificationTag() {
    if (store?.store_marketing_tags?.gmc_verification) {
      return store?.store_marketing_tags?.gmc_verification;
    }
    if (shouldBlockRobots(hostname)) {
      return 'mK1bfkPj0S87QshVGQYkh19-SjVByGSE7V976iYPeLs';
    }
    return '';
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        {/* use to avoid google crawl */}
        {shouldBlockRobots(hostname) && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={OGTitle || title} />
        <meta property="og:description" content={OGDescription || metaDescription} />
        <meta property="og:image" content={OGImage || Icon || DEFAULT_SSR_IMAGE_URL} />
        <meta property="og:image:width" content="300" />
        <meta property="og:image:height" content="300" />
        {/* Twitter Meta Tags  */}
        <meta name="twitter:title" content={OGTitle || title} id="twittertitle" />
        <meta
          name="twitter:description"
          content={OGDescription || metaDescription}
          id="twitterdescription"
        />
        <meta
          name="twitter:image"
          content={OGImage || Icon || DEFAULT_SSR_IMAGE_URL}
          id="twitterimage"
        />
        <meta name="twitter:card" content="summary" />
        {/** Google Site Verification */}
        <meta name="google-site-verification" content={getGoogleSiteVerificationTag()} />

        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer', "${
        store?.store_marketing_tags?.g_tag || 'GTM-N8T6CPZ'
      }")`,
          }}
        />

        {store?.store_marketing_tags?.fb_domain_verification && (
          <meta
            name="facebook-domain-verification"
            content={store?.store_marketing_tags?.fb_domain_verification}
          />
        )}
        {/* Adding canonical link that points to current page in SEO */}
        <link
          rel="canonical"
          href={
            canonicalTagUrl ||
            `${store?.store_info?.store_url}${getDefaultCanonicalUrl(router?.asPath)}`
          }
        />
        <link
          rel="shortcut icon"
          type="image/png"
          href={storeData?.favicon || DEFAULT_SSR_IMAGE_URL}
        />
        <link
          rel="apple-touch-icon"
          sizes="192x192"
          href={store?.store_info?.logo_image || storeData?.favicon || DEFAULT_SSR_IMAGE_URL}
        />
        <link
          rel="apple-touch-icon"
          sizes="512x512"
          href={store?.store_info?.logo_image || storeData?.favicon || DEFAULT_SSR_IMAGE_URL}
        />
        {isBuilder &&
          FONT_DROPDOWN?.map((fontData) => (
            <link
              key={fontData?.fontCdn}
              href={fontData?.fontCdn}
              rel="stylesheet"
              crossOrigin="anonymous"
            />
          ))}
        <link
          rel="manifest"
          href={`/api/manifest?domain=${encodeURIComponent(
            store?.store_info?.domain
          )}&banner=${encodeURIComponent(store?.store_info?.name)}&logo=${encodeURI(
            store?.store_info?.logo_image
          )}&favicon=${encodeURI(
            storeData?.favicon || ''
          )}&url=${encodeURI(store?.store_info?.store_url)}`}
        />

        <AnalyticsTags store={store} featureLocksStatus={featureLocksStatus} />

        {/* <!-- Matomo --> */}
        {/* experimental matomo code for temporary testing */}
        {store?.domain_info?.site_id ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `
              var _paq = window._paq = window._paq || [];
              /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
              _paq.push(['trackPageView']);
              _paq.push(['enableLinkTracking']);
              (function() {
                var u="//matomo.dotshowroom.in/";
                _paq.push(['setTrackerUrl', u+'matomo.php']);
                _paq.push(['setSiteId', ${store?.domain_info?.site_id}]);
                var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
                g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
              })();
            `,
            }}
          />
        ) : null}
        {/* <!-- End Matomo Code */}
      </Head>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
      <div className="dd-showrom__layout">
        <ThemeCss />
        {(isHomePage || isAboutPage) && <RichSnippetStoreDetails store={store} />}

        {store?.domain_info?.status !== 2 && toggleMobileView ? (
          !IS_SERVER && (
            <div className="mobile-screen relative">
              <div className="inaccessibleLayer-login flex items-center">
                <div>
                  <p>If you are the owner of this website then</p>
                  <p>login & enable desktop theme</p>
                </div>
                <span
                  className="login-btn pointer relative dib ml3"
                  onClick={loginBtnClick}
                >
                  Log In
                </span>
              </div>
              <section className="flex">
                <iframe src={window.location.href} height={'100%'} width={'100%'} />
              </section>
            </div>
          )
        ) : (
          <>{children}</>
        )}
        {!!showState.showInaccessibleView ? (
          <InaccessibleLayer
            store={store}
            show={showState.showInaccessibleView}
            redirectToBadShowcase={showIframeView}
          />
        ) : null}
        {!!showState.showRedirectionModal ? (
          <RedirectionToWebConsoleModal show={showState.showRedirectionModal} />
        ) : null}
        {storeData?.services?.store_type_flag === B2B_STORE_TYPE_CONSTANT ? (
          <B2bRfqFormModal productData={props?.product || {}} />
        ) : null}
        {storeData?.services?.exit_gating_flag && !isLoginByEmail ? (
          <ExitGatingModal />
        ) : null}
        {promoModalState?.show && <PromoModal />}
        {promoRemovalAlertModalState?.show && <PromoRemovalAlertModal />}
        {showWhatsAppLeadModal && <WhatsAppLeadGenerationModal />}
      </div>
    </>
  );
};

export default Layout;
